import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Form, Input, Modal, Radio, Space, Typography, message as toast } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { useFeed } from './shared/context';
import { REPORT_REPLY_TEMPLATES, RESTRICT_SCOPE_OPTIONS, TARGET_TYPE_LABEL, replyGroupOf } from './shared/labels';
import { FeedReportGroupRow, ReportResolution, RestrictionScope } from './shared/types';
import { errorCode, feedError } from './shared/utils';
import { DEFAULT_DURATION, DurationPicker, DurationValue, durationDays, untilText } from './restrict-modal';

const defaultScopeFor = (t: FeedReportGroupRow['targetType']): RestrictionScope =>
  t === 'POST' ? 'POST' : t === 'COMMENT' ? 'COMMENT' : 'ALL';

/** Xử lý mọi báo cáo OPEN của một nội dung (D16, 03 §8.3). */
const ResolveModal = ({
  group,
  onClose,
  onViewFull,
}: {
  group: FeedReportGroupRow | null;
  onClose: () => void;
  onViewFull?: (group: FeedReportGroupRow) => void;
}) => {
  const { afterAction, reportReasonLabel } = useFeed();
  const [action, setAction] = useState<ReportResolution>('DISMISS');
  const [note, setNote] = useState('');
  const [scope, setScope] = useState<RestrictionScope>('POST');
  const [duration, setDuration] = useState<DurationValue>(DEFAULT_DURATION);
  const [hideTarget, setHideTarget] = useState(true);
  const [saving, setSaving] = useState(false);
  // Phản hồi người báo cáo: mặc định gửi, điền sẵn câu ★ của nhóm; chưa gõ tay thì đổi theo cách xử lý.
  const [notifyReporters, setNotifyReporters] = useState(true);
  const [reply, setReply] = useState(REPORT_REPLY_TEMPLATES.NO_VIOLATION[0].text);
  const [replyEdited, setReplyEdited] = useState(false);
  const replyRef = useRef<TextAreaRef>(null);

  useEffect(() => {
    if (!group) return;
    setAction('DISMISS');
    setNote('');
    setScope(defaultScopeFor(group.targetType));
    setDuration(DEFAULT_DURATION);
    setHideTarget(true);
    setNotifyReporters(true);
    setReply(REPORT_REPLY_TEMPLATES.NO_VIOLATION[0].text);
    setReplyEdited(false);
  }, [group]);

  if (!group) return null;
  const isUser = group.targetType === 'USER';
  const isDeleted = group.targetStatus === 'DELETED';
  const trimmed = note.trim();
  const needsNote = action !== 'DISMISS';
  const replyTemplates = REPORT_REPLY_TEMPLATES[replyGroupOf(action)];
  const replyTrimmed = reply.trim();
  const replyError = !notifyReporters
    ? undefined
    : replyTrimmed.length < 3
    ? 'Câu phản hồi tối thiểu 3 ký tự (hoặc bỏ chọn "Gửi phản hồi").'
    : /:$/.test(replyTrimmed)
    ? 'Ghi lý do sau dấu hai chấm, hoặc chọn câu không cần lý do.'
    : undefined;
  const invalid =
    (needsNote ? trimmed.length < 3 || trimmed.length > 500 : trimmed.length > 500) || !!replyError;
  const who = group.targetUser?.name || (group.targetUser ? `#${group.targetUser.userId}` : '');

  const changeAction = (next: ReportResolution) => {
    setAction(next);
    if (!replyEdited && replyGroupOf(next) !== replyGroupOf(action)) {
      setReply(REPORT_REPLY_TEMPLATES[replyGroupOf(next)][0].text);
    }
  };

  const pickTemplate = (text: string) => {
    setReply(text);
    setReplyEdited(false);
    // Mẫu "…lý do: " → đưa con trỏ về cuối để gõ tiếp lý do.
    if (/:\s*$/.test(text)) setTimeout(() => replyRef.current?.focus({ cursor: 'end' }), 0);
  };

  const submit = async () => {
    if (invalid) return;
    setSaving(true);
    const days = durationDays(duration);
    try {
      const body: Record<string, unknown> = {
        targetType: group.targetType,
        targetId: group.targetId,
        action,
      };
      if (trimmed) body.note = trimmed;
      if (action === 'RESTRICT') body.restrict = { scope, days, hideTarget: isUser ? false : hideTarget };
      if (notifyReporters) body.replyToReporter = replyTrimmed;
      else body.notifyReporters = false;
      const res: any = await AxiosUtils.post(API.feedAdminReportsResolve, body);
      const resolved = res?.data?.resolved ?? group.reportCount;
      let text = `Đã xử lý ${resolved} báo cáo.`;
      if (action === 'RESTRICT') text += ` ${who || 'Người đăng'} bị hạn chế ${untilText(null, days)}.`;
      const replied = Number(res?.data?.replied) || 0;
      if (notifyReporters && replied) text += ` Đã gửi phản hồi cho ${replied} người báo cáo.`;
      toast.success(text);
      onClose();
      afterAction();
    } catch (err) {
      if (errorCode(err) === 'FEED_REPORT_ALREADY_RESOLVED') {
        toast.info('Báo cáo này vừa được người khác xử lý.');
        onClose();
        afterAction();
      } else {
        toast.error(feedError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const title = `Xử lý báo cáo · ${TARGET_TYPE_LABEL[group.targetType] || group.targetType}${
    isUser ? '' : ` #${group.targetId}`
  }${who ? ` của ${who}` : ''}`;

  return (
    <Modal
      open
      width={640}
      title={title}
      onCancel={onClose}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>
          Huỷ
        </Button>,
        <Button
          key="ok"
          type="primary"
          danger={action === 'DELETE' || action === 'RESTRICT'}
          loading={saving}
          disabled={invalid}
          onClick={submit}
        >
          Xác nhận
        </Button>,
      ]}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          padding: 12,
          borderRadius: 10,
          background: colors.surfaceMuted,
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 4 }}>
            {group.preview ? `“${group.preview}”` : <span style={{ color: colors.textMuted }}>(không có chữ)</span>}
          </Typography.Paragraph>
          <div style={{ fontSize: 13, color: colors.textSecondary }}>
            {group.reporterCount} người báo cáo · {(group.reasons || []).map(reportReasonLabel).join(' · ')}
          </div>
        </div>
        {onViewFull && group.targetType !== 'USER' && (
          <Button size="small" onClick={() => onViewFull(group)}>
            Xem đầy đủ
          </Button>
        )}
      </div>

      <Form layout="vertical">
        <Form.Item label="Cách xử lý">
          <Radio.Group value={action} onChange={(e) => changeAction(e.target.value)}>
            <Space direction="vertical" size={6}>
              <Radio value="DISMISS">
                Không vi phạm — bỏ qua các báo cáo
                {group.targetStatus === 'HIDDEN' && (
                  <div style={{ fontSize: 12, color: colors.textSecondary }}>
                    Nếu nội dung đang tự ẩn vì báo cáo, nó sẽ hiện lại cho mọi người.
                  </div>
                )}
              </Radio>
              {!isUser && (
                <Radio value="HIDE" disabled={isDeleted}>
                  Ẩn nội dung
                </Radio>
              )}
              {!isUser && (
                <Radio value="DELETE" disabled={isDeleted}>
                  Xoá nội dung
                </Radio>
              )}
              <Radio value="RESTRICT">Hạn chế người đăng</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {action === 'RESTRICT' && (
          <div style={{ marginLeft: 24, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Space wrap>
              <span style={{ minWidth: 80 }}>Không được:</span>
              <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
                {RESTRICT_SCOPE_OPTIONS.map((o) => (
                  <Radio key={o.value} value={o.value}>
                    {o.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Space>
            <Space wrap>
              <span style={{ minWidth: 80 }}>Trong:</span>
              <DurationPicker value={duration} onChange={setDuration} />
            </Space>
            {!isUser && (
              <Checkbox checked={hideTarget} onChange={(e) => setHideTarget(e.target.checked)} disabled={isDeleted}>
                Ẩn luôn nội dung này
              </Checkbox>
            )}
          </div>
        )}

        <Form.Item
          label={needsNote ? 'Lý do gửi người đăng' : 'Ghi chú nội bộ'}
          required={needsNote}
          extra={
            needsNote
              ? 'Tác giả thấy lý do này khi bị ẩn/hạn chế. Ghi vào Nhật ký.'
              : 'Không bắt buộc. Chỉ ghi vào Nhật ký.'
          }
          validateStatus={needsNote && note && trimmed.length < 3 ? 'error' : undefined}
          help={needsNote && note && trimmed.length < 3 ? 'Lý do tối thiểu 3 ký tự.' : undefined}
        >
          <Input.TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 5 }}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          label={`Phản hồi cho người báo cáo (${group.reporterCount} người)`}
          extra={
            notifyReporters
              ? 'Gửi vào mục Thông báo của từng người báo cáo, kèm nội dung họ đã báo cáo và kết quả. Không ai thấy tên người báo cáo khác.'
              : 'Người báo cáo sẽ không nhận thông báo nào.'
          }
          validateStatus={replyError ? 'error' : undefined}
          help={replyError}
        >
          <Checkbox
            checked={notifyReporters}
            onChange={(e) => setNotifyReporters(e.target.checked)}
            style={{ marginBottom: notifyReporters ? 8 : 0 }}
          >
            Gửi phản hồi
          </Checkbox>
          {notifyReporters && (
            <>
              <Space size={[6, 6]} wrap style={{ display: 'flex', marginBottom: 8 }}>
                {replyTemplates.map((t) => (
                  <Button
                    key={t.label}
                    size="small"
                    type={reply === t.text ? 'primary' : 'default'}
                    ghost={reply === t.text}
                    onClick={() => pickTemplate(t.text)}
                  >
                    {t.label}
                  </Button>
                ))}
              </Space>
              <Input.TextArea
                ref={replyRef}
                value={reply}
                onChange={(e) => {
                  setReply(e.target.value);
                  setReplyEdited(true);
                }}
                autoSize={{ minRows: 2, maxRows: 5 }}
                maxLength={500}
                showCount
              />
            </>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ResolveModal;
